import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { t } from "ttag";

import { trackDqErrorShown } from "@/features/analytics/trackers/dq-error-shown";
import { trackGlp1User } from "@/features/analytics/trackers/patient-profile-captured";
import { FIELD_TYPE } from "@/features/forms/models";
import { LogoLoader } from "@/shared/components";
import { FunnelIntakeQuestion, FunnelStep } from "../components";
import { useFunnelIntakeScreens } from "../hooks";
import type {
  FunnelIntakeAnswers,
  FunnelIntakeStep,
  FunnelIntakeQuestion as Question,
} from "../models";
import { INTAKE_QUESTION_KEY } from "../models";
import { FUNNEL_ROUTE, funnelDisqualifiedRoute } from "../router/constants";
import { useFunnelStore } from "../stores";
import type { FunnelIntakeQuestionIndex } from "../utils";
import {
  firstIntakeRoute,
  hasBlockingMessage,
  hasDisqualifyingAnswer,
  isQuestionVisible,
  isStepComplete,
  nextIntakeRoute,
  pruneHiddenAnswers,
  visibleQuestions,
} from "../utils";

interface FunnelIntakeStepFormProps {
  step: FunnelIntakeStep;
  steps: FunnelIntakeStep[];
  questionsByKey: FunnelIntakeQuestionIndex;
}

const chosenOption = (question: Question, value: string | string[]) => {
  if (question.input === FIELD_TYPE.RADIO && typeof value === "string") {
    return value;
  }

  const [only] = Array.isArray(value) && value.length === 1 ? value : [];

  return question.options.some(
    (option) => option.value === only && option.exclusive,
  )
    ? only
    : undefined;
};

const choiceNeedsMore = (
  question: Question,
  value: string,
  answers: FunnelIntakeAnswers,
  questionsByKey: FunnelIntakeQuestionIndex,
) => {
  const option = question.options.find((current) => current.value === value);

  return (
    option !== undefined &&
    (option.blockingMessage !== null ||
      option.followUps.some((followUp) =>
        isQuestionVisible(followUp, answers, questionsByKey),
      ))
  );
};

const FunnelIntakeStepForm = ({
  step,
  steps,
  questionsByKey,
}: FunnelIntakeStepFormProps) => {
  const navigate = useNavigate();
  const set = useFunnelStore((store) => store.set);
  const [answers, setAnswers] = useState<FunnelIntakeAnswers>(() =>
    pruneHiddenAnswers(
      useFunnelStore.getState().intakeAnswers ?? {},
      questionsByKey,
    ),
  );

  const questions = visibleQuestions(step, answers, questionsByKey);
  const showsBlockingMessage = hasBlockingMessage(
    step,
    answers,
    questionsByKey,
  );

  useEffect(() => {
    if (showsBlockingMessage) {
      trackDqErrorShown(step.slug);
    }
  }, [showsBlockingMessage, step.slug]);

  const submit = (submitted: FunnelIntakeAnswers) => {
    set({ intakeAnswers: submitted });
    const medicationQuestion = step.questions.find(
      (question) => question.key === INTAKE_QUESTION_KEY.WEIGHT_LOSS_MEDICATION,
    );
    if (medicationQuestion) {
      const pickedOption = medicationQuestion.options.find(
        (option) => option.value === submitted[medicationQuestion.key],
      );
      trackGlp1User(
        pickedOption?.followUps.some(
          (followUp) => followUp.key === INTAKE_QUESTION_KEY.GLP1_MEDICATION,
        ) ?? false,
      );
    }
    navigate(
      hasDisqualifyingAnswer(step, submitted, questionsByKey)
        ? funnelDisqualifiedRoute(step.slug)
        : nextIntakeRoute(steps, step.slug, submitted, questionsByKey),
    );
  };

  const handleChange = (key: string, value: string | string[]) => {
    const next = pruneHiddenAnswers(
      { ...answers, [key]: value },
      questionsByKey,
    );
    setAnswers(next);

    const question = questionsByKey.get(key);
    const choice = question && chosenOption(question, value);
    const advancesOnChoice =
      question !== undefined &&
      choice !== undefined &&
      !choiceNeedsMore(question, choice, next, questionsByKey) &&
      isStepComplete(step, next, questionsByKey);

    if (advancesOnChoice) {
      submit(next);
    }
  };

  return (
    <FunnelStep
      title={step.title}
      description={step.description}
      footerNote={step.footerNote}
      nextDisabled={!isStepComplete(step, answers, questionsByKey)}
      onSubmit={() => submit(answers)}
    >
      <div className="grid gap-8">
        {questions.map((question) => (
          <FunnelIntakeQuestion
            key={question.key}
            question={question}
            answers={answers}
            questionsByKey={questionsByKey}
            onChange={handleChange}
          />
        ))}
      </div>
    </FunnelStep>
  );
};

export const FunnelIntake = () => {
  const { slug } = useParams();
  const sex = useFunnelStore((store) => store.sex);
  const intakeAnswers = useFunnelStore((store) => store.intakeAnswers);
  const {
    params,
    query: { data, isPending, isError, refetch, isRefetching },
    questionsByKey,
  } = useFunnelIntakeScreens();

  if (!sex) {
    return <Navigate to={FUNNEL_ROUTE.PERSONAL_INFO} replace />;
  }

  if (!params) {
    return <Navigate to={FUNNEL_ROUTE.MEASUREMENTS} replace />;
  }

  if (isError) {
    return (
      <FunnelStep
        title={t`We couldn't load your questions.`}
        description={t`Please check your connection and try again.`}
        nextLabel={t`Try again`}
        isSubmitting={isRefetching}
        onSubmit={() => void refetch()}
      />
    );
  }

  if (isPending) {
    return <LogoLoader show fullscreen />;
  }

  const answers = intakeAnswers ?? {};
  const step = data.steps.find((current) => current.slug === slug);

  if (data.steps.length === 0) {
    return <Navigate to={FUNNEL_ROUTE.PERSONAL_INFO} replace />;
  }

  if (!step || visibleQuestions(step, answers, questionsByKey).length === 0) {
    return (
      <Navigate
        to={firstIntakeRoute(data.steps, answers, questionsByKey)}
        replace
      />
    );
  }

  return (
    <FunnelIntakeStepForm
      key={step.slug}
      step={step}
      steps={data.steps}
      questionsByKey={questionsByKey}
    />
  );
};
